// ---------------------------------------------------------------------------
// TanStack Query hooks for approval workflow API
// ---------------------------------------------------------------------------

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ReviewComment, TransitionRequest, TransitionResponse, CreateCommentRequest, UpdateCommentRequest, CmsRole } from '@/types/workflow';
import { REVIEW_QUEUE_STATUSES } from '@/types/workflow';
import type { Template, TemplateStatus } from '@/types/template';
import type { PaginatedResponse } from '@/types/common';

const WORKFLOW_KEY = 'workflow';
const TEMPLATES_KEY = 'templates';
const APPROVAL_QUEUE_KEY = 'approval-queue';

// SE-API-18: /comments is now paginated. The thread renders the full list with
// no page controls, so request a page large enough to cover any realistic
// thread (there is no server-imposed cap). A "load more" control is a follow-up
// if threads ever exceed this.
const COMMENT_PAGE_SIZE = 200;

export function useWorkflowHistory(templateId: string | undefined) {
  return useQuery({
    queryKey: [WORKFLOW_KEY, templateId],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<ReviewComment>>(
        `/comments?templateId=${templateId}&pageSize=${COMMENT_PAGE_SIZE}`,
      );
      return res.entries;
    },
    enabled: !!templateId,
  });
}

export function useTransitionTemplate(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TransitionRequest) =>
      api
        .post<{ entries: TransitionResponse[] }>(`/template/transition`, {
          entries: [{ templateId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TEMPLATES_KEY] });
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_KEY, templateId] });
    },
  });
}

export function useAddComment(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCommentRequest) =>
      api
        .post<{ entries: ReviewComment[] }>(`/comment`, {
          entries: [{ templateId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_KEY, templateId] });
    },
  });
}

export function useEditComment(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      commentId,
      ...data
    }: UpdateCommentRequest & { commentId: string }) =>
      api
        .patch<{ entries: ReviewComment[] }>(`/comment`, {
          entries: [{ id: commentId, ...data }],
        })
        .then((r) => r.entries[0]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_KEY, templateId] });
    },
  });
}

export function useDeleteComment(templateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      api.delete(`/comment`, { entries: [{ id: commentId }] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [WORKFLOW_KEY, templateId] });
    },
  });
}

// ---------------------------------------------------------------------------
// CMS-03a — Approval queue hooks
// ---------------------------------------------------------------------------
// Polls the template list filtered by the reviewer's queue statuses every
// 30s (locked decision 2026-05-28; TanStack Query refetchInterval).

interface QueueFetchResult {
  templates: Template[];
  totalCount: number;
}

async function fetchQueueForStatuses(
  statuses: TemplateStatus[],
): Promise<QueueFetchResult> {
  // The list endpoint accepts a single status param. Fan out to multiple
  // calls when the reviewer's queue covers more than one status, then merge.
  const results = await Promise.all(
    statuses.map((s) =>
      api.get<PaginatedResponse<Template>>(
        `/templates?status=${s}&pageSize=100`,
      ),
    ),
  );
  const templates = results.flatMap((r) => r.entries);
  // Total across statuses; assumes <100 per status in practice (no pagination
  // needed for a review queue this small).
  const totalCount = results.reduce(
    (acc, r) => acc + (r.total ?? r.entries.length),
    0,
  );
  return { templates, totalCount };
}

/**
 * Fetches the templates currently sitting in this role's review queue.
 * Refetches every 30s so the queue surface stays close to real-time.
 */
export function useApprovalQueue(role: CmsRole | undefined) {
  const statuses = (role && REVIEW_QUEUE_STATUSES[role]) ?? [];
  return useQuery({
    queryKey: [APPROVAL_QUEUE_KEY, role, statuses],
    queryFn: () => fetchQueueForStatuses(statuses),
    enabled: statuses.length > 0,
    refetchInterval: 30_000,
  });
}

/**
 * Lighter version: returns just the count. Used by the sidebar badge.
 */
export function useApprovalQueueCount(role: CmsRole | undefined): {
  count: number;
  isLoading: boolean;
} {
  const { data, isLoading } = useApprovalQueue(role);
  return { count: data?.totalCount ?? 0, isLoading };
}
